import { Component, computed, input } from '@angular/core';

import { InfoscreenWeatherCondition } from '../infoscreen-module.types';

/** The weather in words, as the screens write it. */
export const WEATHER_LABELS: Record<InfoscreenWeatherCondition, string> = {
  Clear: 'Klart',
  Fair: 'Let skyet',
  PartlyCloudy: 'Delvist skyet',
  Cloudy: 'Overskyet',
  Fog: 'Tåge',
  Rain: 'Regn',
  Sleet: 'Slud',
  Snow: 'Sne',
  Thunder: 'Torden',
};

/**
 * A weather symbol, drawn from a few shapes: the sun (or the moon at night), a cloud, rain drops,
 * snowflakes, a bolt and fog. Sized by the font size (1em), so it sits in a line of text.
 */
@Component({
  selector: 'app-weather-icon',
  template: `
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" [attr.aria-label]="label()" role="img">
      @if (showSun()) {
        @if (night()) {
          <path [attr.transform]="sunTransform()" d="M30 8a22 22 0 1 0 24 30A18 18 0 0 1 30 8z" fill="#f4efd2" />
        } @else {
          <g [attr.transform]="sunTransform()">
            <circle cx="32" cy="32" r="12" fill="#ffd23f" />
            <path
              d="M32 8v8M32 48v8M8 32h8M48 32h8M15 15l6 6M43 43l6 6M15 49l6-6M43 21l6-6"
              stroke="#ffd23f"
              stroke-width="4"
              stroke-linecap="round"
            />
          </g>
        }
      }
      @if (showCloud()) {
        <path
          d="M18 50h30a11 11 0 0 0 0-22 15 15 0 0 0-28-3 10 10 0 0 0-2 25z"
          [attr.fill]="darkCloud() ? '#9aa6b8' : '#eef2f7'"
          stroke="rgba(0,0,0,0.08)"
        />
      }
      @switch (condition()) {
        @case ('Rain') {
          <path d="M22 54l-3 7M33 54l-3 7M44 54l-3 7" stroke="#5cc8f0" stroke-width="3.5" stroke-linecap="round" />
        }
        @case ('Sleet') {
          <path d="M24 54l-3 7M42 54l-3 7" stroke="#5cc8f0" stroke-width="3.5" stroke-linecap="round" />
          <path d="M32 54v8M28.5 56l7 4M28.5 60l7-4" stroke="#ffffff" stroke-width="2" stroke-linecap="round" />
        }
        @case ('Snow') {
          <g stroke="#ffffff" stroke-width="2" stroke-linecap="round">
            <path d="M22 53v8M18.5 55l7 4M18.5 59l7-4" />
            <path d="M42 53v8M38.5 55l7 4M38.5 59l7-4" />
          </g>
        }
        @case ('Thunder') {
          <path d="M34 50l-8 9h6l-4 8 10-11h-6l4-6z" fill="#ffd23f" />
        }
        @case ('Fog') {
          <path d="M10 40h44M14 48h36M10 56h44" stroke="#dfe5ec" stroke-width="4" stroke-linecap="round" />
        }
      }
    </svg>
  `,
  styles: `
    :host {
      display: inline-block;
      width: 1em;
      height: 1em;
      vertical-align: -0.12em;
    }
    svg {
      width: 100%;
      height: 100%;
      overflow: visible;
    }
  `,
})
export class WeatherIconComponent {
  readonly condition = input.required<InfoscreenWeatherCondition>();
  readonly night = input(false);

  readonly label = computed(() => WEATHER_LABELS[this.condition()]);
  readonly showSun = computed(() => ['Clear', 'Fair', 'PartlyCloudy'].includes(this.condition()));
  readonly showCloud = computed(() => this.condition() !== 'Clear');
  readonly darkCloud = computed(() => ['Rain', 'Sleet', 'Thunder'].includes(this.condition()));
  /** With a cloud in front, the sun (or moon) is smaller and up in the corner. */
  readonly sunTransform = computed(() => (this.condition() === 'Clear' ? '' : 'translate(4 -6) scale(0.7)'));
}
